import { useQuery } from "react-query";
import { fetchData } from "../api/client";
import { getVisibleFieldDefinitions } from "../components/CustomFieldsBlock";
import {
  logFieldDefinitions,
  summarizeFieldDefsPayload,
} from "../utils/fieldDefinitionsDebug";

/** Metadados mínimos dos slots legado em leads (paridade com field-definitions da API). */
export const REQUEST_LEGACY_FALLBACK = [
  {
    key: "produto",
    legacyConfigKey: "nomeproduto",
    defaultLabel: "Produto",
    type: "select",
    requiresLabel: true,
  },
  {
    key: "campanha",
    legacyConfigKey: "nomecampanha",
    defaultLabel: "Campanha",
    type: "select",
    requiresLabel: true,
  },
  {
    key: "data_adicional",
    legacyConfigKey: "dataadicionallead",
    defaultLabel: "Data Adicional",
    type: "datetime",
    requiresLabel: true,
  },
  {
    key: "texto_adicional",
    legacyConfigKey: "textoadicionallead",
    defaultLabel: "Campo Adicional",
    type: "text",
    requiresLabel: true,
  },
];

function trimLabel(v) {
  if (v == null) return "";
  return String(v).trim();
}

/** Ordem dos slots legado na solicitação (paridade lead.phtml). */
export const LEGACY_REQUEST_FIELD_ORDER = [
  "produto",
  "campanha",
  "data_adicional",
  "texto_adicional",
];

export function isFieldDefinitionVisible(def) {
  if (!def) return false;
  const v = def.visible;
  if (v === false || v === 0 || v === "0" || v === "false") return false;
  return true;
}

function sortByLegacyRequestOrder(fields) {
  return [...(fields || [])].sort((a, b) => {
    const ia = LEGACY_REQUEST_FIELD_ORDER.indexOf(a?.key);
    const ib = LEGACY_REQUEST_FIELD_ORDER.indexOf(b?.key);
    if (ia === -1 && ib === -1) {
      return (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0);
    }
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

function enrichLegacyFieldDefinition(def, configByKey) {
  if (!def?.legacyConfigKey || typeof configByKey !== "function") return def;
  const labelRaw = trimLabel(configByKey(def.legacyConfigKey));
  if (!labelRaw) return def;
  return { ...def, label: labelRaw };
}

/** Fallback local a partir de /configurations quando a API ainda não respondeu ou falhou. */
export function buildLegacyRequestFallback(byKey) {
  if (typeof byKey !== "function") {
    return REQUEST_LEGACY_FALLBACK.map((f) => ({
      key: f.key,
      label: f.defaultLabel,
      type: f.type,
      visible: !f.requiresLabel,
      legacy: true,
      legacyConfigKey: f.legacyConfigKey,
    }));
  }
  const hasOtherLeadLabels = REQUEST_LEGACY_FALLBACK.filter(
    (o) => o.key !== "data_adicional" && o.requiresLabel
  ).some((o) => trimLabel(byKey(o.legacyConfigKey)));

  return REQUEST_LEGACY_FALLBACK.map((f) => {
    const labelRaw = trimLabel(byKey(f.legacyConfigKey));
    let visible = !(f.requiresLabel && !labelRaw);
    // /configurations pode não trazer dataadicionallead no cache; se Segmento/Trechos existem, exibir data.
    if (
      f.requiresLabel &&
      !labelRaw &&
      f.key === "data_adicional" &&
      hasOtherLeadLabels
    ) {
      visible = true;
    }
    return {
      key: f.key,
      label: labelRaw || f.defaultLabel,
      type: f.type,
      visible,
      legacy: true,
      legacyConfigKey: f.legacyConfigKey,
    };
  });
}

/**
 * Normaliza o retorno do react-query / fetchData para { request, client, meta }.
 */
export function parseFieldDefinitionsPayload(fieldDefs) {
  logFieldDefinitions("parse:in", summarizeFieldDefsPayload(fieldDefs));

  if (!fieldDefs) {
    logFieldDefinitions("parse:out", { reason: "null-input" });
    return { request: [], client: [], meta: {} };
  }

  let x = fieldDefs;

  if (x.data != null && typeof x.data === "object" && !Array.isArray(x.data)) {
    if (Array.isArray(x.data.request) || Array.isArray(x.data.client)) {
      x = x.data;
    } else if (x.data.data != null && typeof x.data.data === "object") {
      x = x.data.data;
    }
  }

  if (Array.isArray(x)) {
    return { request: x, client: [], meta: {} };
  }

  if (Array.isArray(x.request) || Array.isArray(x.client)) {
    const out = {
      request: Array.isArray(x.request) ? x.request : [],
      client: Array.isArray(x.client) ? x.client : [],
      meta: x.meta && typeof x.meta === "object" ? x.meta : {},
    };
    logFieldDefinitions("parse:out", summarizeFieldDefsPayload(out));
    return out;
  }

  // Resposta HTTP bruta { message, data: { request, client } } sem unwrap do fetchData
  if (x.message != null && x.data != null && typeof x.data === "object") {
    return parseFieldDefinitionsPayload(x.data);
  }

  logFieldDefinitions("parse:out", { reason: "no-request-array", xKeys: Object.keys(x) });
  return { request: [], client: [], meta: {} };
}

/**
 * Campos visíveis para a UI da solicitação.
 */
function indexRequestFieldDefinitions(apiList) {
  const apiByKey = new Map();
  for (const d of apiList) {
    if (!d?.key) continue;
    const isLegacySlot = LEGACY_REQUEST_FIELD_ORDER.includes(d.key);
    const prev = apiByKey.get(d.key);
    if (!prev) {
      apiByKey.set(d.key, d);
      continue;
    }
    // Slot legado (leads.*) prevalece sobre definição EAV com mesmo field_key
    if (isLegacySlot && d.legacy && !prev.legacy) {
      apiByKey.set(d.key, d);
    }
  }
  return apiByKey;
}

export function resolveVisibleRequestFields({
  apiRequest = [],
  configByKey,
} = {}) {
  const apiList = Array.isArray(apiRequest) ? apiRequest : [];

  if (apiList.length > 0) {
    const apiByKey = indexRequestFieldDefinitions(
      apiList.filter((d) => d && d.key)
    );
    const ordered = [];
    for (const key of LEGACY_REQUEST_FIELD_ORDER) {
      const def = apiByKey.get(key);
      if (def && isFieldDefinitionVisible(def)) {
        ordered.push(enrichLegacyFieldDefinition(def, configByKey));
      }
    }
    for (const def of apiList) {
      if (!def?.key || LEGACY_REQUEST_FIELD_ORDER.includes(def.key)) continue;
      if (isFieldDefinitionVisible(def) && !ordered.some((o) => o.key === def.key)) {
        ordered.push(def);
      }
    }
    if (ordered.length > 0) {
      logFieldDefinitions("resolve:from-api", {
        count: ordered.length,
        keys: ordered.map((d) => d.key),
      });
      return ordered;
    }
  }

  const fromFallback = sortByLegacyRequestOrder(
    getVisibleFieldDefinitions(buildLegacyRequestFallback(configByKey))
  );
  logFieldDefinitions("resolve:from-fallback", {
    count: fromFallback.length,
    keys: fromFallback.map((d) => d.key),
    configByKeyIsFunction: typeof configByKey === "function",
  });
  return fromFallback;
}

/**
 * Definições de campos legado + EAV para solicitação e cliente.
 */
export function useFieldDefinitions(params = {}) {
  const {
    typeId,
    subtype1,
    subtype2,
    subtype3,
    projectId,
    leadId,
    context,
    enabled = true,
  } = params;

  const queryKey = [
    "field-definitions",
    "v5",
    context || "",
    typeId,
    subtype1,
    subtype2,
    subtype3,
    projectId,
    leadId,
  ];

  return useQuery(
    queryKey,
    async () => {
      const qs = new URLSearchParams();
      if (typeId != null && typeId !== "") qs.set("typeId", String(typeId));
      if (subtype1 != null && subtype1 !== "") qs.set("subtype1", String(subtype1));
      if (subtype2 != null && subtype2 !== "") qs.set("subtype2", String(subtype2));
      if (subtype3 != null && subtype3 !== "") qs.set("subtype3", String(subtype3));
      if (projectId != null && projectId !== "") qs.set("projectId", String(projectId));
      if (leadId != null && leadId !== "") qs.set("leadId", String(leadId));
      if (context != null && String(context).trim() !== "") {
        qs.set("context", String(context).trim());
      }
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const url = `/workspace/field-definitions${suffix}`;
      logFieldDefinitions("query:start", { url, typeId, leadId, projectId });

      const res = await fetchData(url, "GET");
      logFieldDefinitions("query:fetchData-raw", summarizeFieldDefsPayload(res?.data ?? res));

      const parsed = parseFieldDefinitionsPayload(res?.data ?? res);
      logFieldDefinitions("query:done", {
        requestLen: parsed.request?.length ?? 0,
        visibleKeys: (parsed.request || [])
          .filter((d) => d.visible !== false)
          .map((d) => d.key),
      });
      return parsed;
    },
    {
      enabled: !!enabled,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: 1,
    }
  );
}

/** Busca opções de produto/campanha (paginada; use com debounce no Autocomplete). */
export async function searchRequestFieldOptions({
  field,
  q = "",
  typeId,
  subtype1,
  subtype2,
  subtype3,
  projectId,
  limit = 50,
}) {
  const qs = new URLSearchParams({
    fields: field,
    limit: String(limit),
  });
  if (q) qs.set("q", String(q));
  if (typeId != null && typeId !== "") qs.set("typeId", String(typeId));
  if (subtype1 != null && subtype1 !== "") qs.set("subtype1", String(subtype1));
  if (subtype2 != null && subtype2 !== "") qs.set("subtype2", String(subtype2));
  if (subtype3 != null && subtype3 !== "") qs.set("subtype3", String(subtype3));
  if (projectId != null && projectId !== "") qs.set("projectId", String(projectId));
  const res = await fetchData(`/workspace/request-field-options?${qs.toString()}`, "GET");
  const data = res?.data || res || {};
  const options = data.options || {};
  return options[field] || [];
}
